import React, { useMemo, useState } from "react";
import styled, { css } from "styled-components";
import moment from "moment";
import { Tag, Button } from "../../../../design-system";
import { OutreachLead, LeadStage } from "../../../../types/OutreachMetrics";
import {
  channelLabel,
  dueOf,
  dueSortKey,
  dueTone,
  DueTone,
  StageModel,
} from "./leadUtils";

// ─── styled ───────────────────────────────────────────────────────────────────

const BoardHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-3);
  margin-bottom: var(--space-3);
`;

const Hint = styled.p`
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  color: var(--color-text-tertiary);
  margin: 0;
`;

const Scroller = styled.div`
  display: flex;
  gap: var(--space-3);
  overflow-x: auto;
  padding-bottom: var(--space-3);
  scrollbar-width: thin;

  &::-webkit-scrollbar {
    height: 8px;
  }
  &::-webkit-scrollbar-thumb {
    background: var(--color-border);
    border-radius: var(--radius-full);
  }
`;

const Column = styled.div<{ $over: boolean }>`
  flex: 0 0 268px;
  width: 268px;
  display: flex;
  flex-direction: column;
  background: var(--color-surface-subtle);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  transition: border-color var(--duration-fast) var(--ease-standard),
    box-shadow var(--duration-fast) var(--ease-standard);

  ${(p) =>
    p.$over &&
    css`
      border-color: var(--color-accent);
      box-shadow: 0 0 0 1px var(--color-accent);
    `}
`;

const ColumnHead = styled.div`
  position: sticky;
  top: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-2);
  padding: var(--space-3);
  background: var(--color-surface-subtle);
  border-bottom: 1px solid var(--color-border-subtle);
  border-radius: var(--radius-lg) var(--radius-lg) 0 0;
  z-index: 1;
`;

const ColumnTitle = styled.div`
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text);
`;

const Dot = styled.span<{ $c: string }>`
  width: 8px;
  height: 8px;
  border-radius: var(--radius-full);
  background: ${(p) => p.$c};
  flex-shrink: 0;
`;

const ColumnCount = styled.span`
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-tertiary);
  background: var(--color-surface-inset);
  padding: 1px var(--space-2);
  border-radius: var(--radius-full);
`;

const ColumnBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  padding: var(--space-3);
  min-height: 56px;
  max-height: calc(100vh - 340px);
  overflow-y: auto;
  flex: 1;
`;

const CardEl = styled.div<{ $dragging: boolean }>`
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  padding: var(--space-3);
  display: flex;
  flex-direction: column;
  gap: 6px;
  cursor: grab;
  box-shadow: var(--shadow-xs);
  transition: border-color var(--duration-fast) var(--ease-standard),
    box-shadow var(--duration-fast) var(--ease-standard);
  opacity: ${(p) => (p.$dragging ? 0.4 : 1)};

  &:hover {
    border-color: var(--color-accent);
    box-shadow: var(--shadow-sm);
  }
  &:active {
    cursor: grabbing;
  }
`;

const CardTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-2);
`;

const CardName = styled.span`
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const CardMeta = styled.span`
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  color: var(--color-text-tertiary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const CardNext = styled.span`
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  color: var(--color-text-secondary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const DueBadge = styled.span<{ $tone: DueTone }>`
  align-self: flex-start;
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-semibold);
  padding: 2px var(--space-2);
  border-radius: var(--radius-full);
  ${(p) =>
    p.$tone === "overdue"
      ? css`
          color: var(--color-danger);
          background: var(--color-danger-subtle, var(--color-surface-inset));
        `
      : p.$tone === "today"
      ? css`
          color: var(--color-warning);
          background: var(--color-warning-subtle, var(--color-surface-inset));
        `
      : p.$tone === "future"
      ? css`
          color: var(--color-text-secondary);
          background: var(--color-surface-inset);
        `
      : css`
          color: var(--color-text-tertiary);
          background: var(--color-surface-inset);
        `}
`;

// Per-column window: a column with 200 leads renders 30 cards and expands in
// steps, keeping the board light.
const COL_PAGE = 30;

const ColMore = styled.button`
  border: 1px dashed var(--color-border);
  background: transparent;
  border-radius: var(--radius-md);
  padding: var(--space-2);
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  color: var(--color-text-secondary);
  cursor: pointer;

  &:hover {
    color: var(--color-text);
    border-color: var(--color-accent);
  }
`;

const Empty = styled.div<{ $over: boolean }>`
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  color: var(--color-text-tertiary);
  text-align: center;
  padding: var(--space-4) var(--space-2);
  border: 1px dashed
    ${(p) => (p.$over ? "var(--color-accent)" : "var(--color-border)")};
  border-radius: var(--radius-md);
`;

const dueLabel = (due: string | undefined, today: string) =>
  !due
    ? "No date"
    : due < today
    ? moment(due).fromNow()
    : due === today
    ? "Today"
    : moment(due).format("MMM D");

// ─── card ───────────────────────────────────────────────────────────────────

const LeadCard: React.FC<{
  lead: OutreachLead;
  today: string;
  dragging: boolean;
  onSelect: (lead: OutreachLead) => void;
  onDragStart: (lead: OutreachLead) => void;
  onDragEnd: () => void;
}> = ({ lead, today, dragging, onSelect, onDragStart, onDragEnd }) => {
  const due = dueOf(lead);
  const tone = dueTone(due, today);
  const meta =
    [lead.company, lead.niche, lead.handle].filter(Boolean).join(" · ") || "—";
  return (
    <CardEl
      $dragging={dragging}
      draggable
      onClick={() => onSelect(lead)}
      onDragStart={(e) => {
        e.dataTransfer.effectAllowed = "move";
        e.dataTransfer.setData("text/plain", lead.id);
        onDragStart(lead);
      }}
      onDragEnd={onDragEnd}
    >
      <CardTop>
        <CardName>{lead.name}</CardName>
        <Tag variant="neutral" size="sm">
          {channelLabel(lead.channel)}
        </Tag>
      </CardTop>
      <CardMeta>{meta}</CardMeta>
      {lead.nextAction && <CardNext>{lead.nextAction}</CardNext>}
      <DueBadge $tone={tone}>{dueLabel(due, today)}</DueBadge>
    </CardEl>
  );
};

// ─── board ──────────────────────────────────────────────────────────────────

const LeadBoard: React.FC<{
  leads: OutreachLead[];
  today: string;
  model: StageModel;
  onSelect: (lead: OutreachLead) => void;
  onMoveStage: (lead: OutreachLead, stage: LeadStage) => void;
}> = ({ leads, today, model, onSelect, onMoveStage }) => {
  const [dragging, setDragging] = useState<OutreachLead | null>(null);
  const [overStage, setOverStage] = useState<LeadStage | null>(null);
  // How many cards each column shows; missing key = first page.
  const [colShown, setColShown] = useState<Record<string, number>>({});
  // Terminal Won/Lost columns are hidden by default so the board stays focused on
  // active pipeline (and avoids the extra horizontal scroll they add).
  const [showClosed, setShowClosed] = useState(false);

  // Columns = the configured stages, then any "orphan" stages present on leads
  // but no longer in the config (a stage that was renamed/deleted) appended at
  // the end, so a lead's card never silently disappears.
  const columns = useMemo(() => {
    const orphans = Array.from(new Set(leads.map((l) => l.stage))).filter(
      (k) => k && !model.byKey[k]
    );
    return [...model.keys, ...orphans];
  }, [leads, model]);
  const isOrphan = (stage: LeadStage) => !model.byKey[stage];

  // Group leads into stage columns, each sorted by due date (soonest first,
  // no-date last) so the most urgent card sits at the top of every column.
  const byStage = useMemo(() => {
    const map = {} as Record<LeadStage, OutreachLead[]>;
    columns.forEach((s) => (map[s] = []));
    leads.forEach((l) => (map[l.stage] || (map[l.stage] = [])).push(l));
    columns.forEach((s) =>
      map[s].sort((a, b) => dueSortKey(a).localeCompare(dueSortKey(b)))
    );
    return map;
  }, [leads, columns]);

  const closedCount = leads.filter((l) => !model.isActive(l.stage)).length;
  const visibleStages = showClosed
    ? columns
    : columns.filter((s) => model.isActive(s));

  const endDrag = () => {
    setDragging(null);
    setOverStage(null);
  };

  const handleDrop = (stage: LeadStage) => {
    // Orphan columns aren't real stages — dropping there would be rejected by the
    // server, so they're not valid drop targets.
    if (dragging && dragging.stage !== stage && !isOrphan(stage))
      onMoveStage(dragging, stage);
    endDrag();
  };

  return (
    <>
      <BoardHead>
        <Hint>Drag a card to another column to change its stage.</Hint>
        {closedCount > 0 && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowClosed((v) => !v)}
          >
            {showClosed ? "Hide closed" : `Show closed (${closedCount})`}
          </Button>
        )}
      </BoardHead>
      <Scroller>
        {visibleStages.map((stage) => {
          const items = byStage[stage] || [];
          const orphan = isOrphan(stage);
          const over = overStage === stage && !orphan;
          return (
            <Column
              key={stage}
              $over={over}
              onDragOver={(e) => {
                if (!dragging || orphan) return;
                e.preventDefault();
                e.dataTransfer.dropEffect = "move";
                if (overStage !== stage) setOverStage(stage);
              }}
              onDragLeave={(e) => {
                // Only clear when the pointer actually leaves the column, not when
                // it crosses into a child element.
                if (!e.currentTarget.contains(e.relatedTarget as Node))
                  setOverStage((s) => (s === stage ? null : s));
              }}
              onDrop={(e) => {
                e.preventDefault();
                handleDrop(stage);
              }}
            >
              <ColumnHead>
                <ColumnTitle>
                  <Dot $c={orphan ? "var(--color-text-tertiary)" : model.dot(stage)} />
                  {model.label(stage)}
                  {orphan && (
                    <Tag variant="neutral" size="sm">
                      archived
                    </Tag>
                  )}
                </ColumnTitle>
                <ColumnCount>{items.length}</ColumnCount>
              </ColumnHead>
              <ColumnBody>
                {items.length === 0 ? (
                  <Empty $over={over}>{over ? "Drop here" : "—"}</Empty>
                ) : (
                  <>
                    {items
                      .slice(0, colShown[stage] ?? COL_PAGE)
                      .map((l) => (
                        <LeadCard
                          key={l.id}
                          lead={l}
                          today={today}
                          dragging={dragging?.id === l.id}
                          onSelect={onSelect}
                          onDragStart={setDragging}
                          onDragEnd={endDrag}
                        />
                      ))}
                    {items.length > (colShown[stage] ?? COL_PAGE) && (
                      <ColMore
                        type="button"
                        onClick={() =>
                          setColShown((m) => ({
                            ...m,
                            [stage]: (m[stage] ?? COL_PAGE) + COL_PAGE,
                          }))
                        }
                      >
                        Show {Math.min(
                          COL_PAGE,
                          items.length - (colShown[stage] ?? COL_PAGE)
                        )}{" "}
                        more · {items.length - (colShown[stage] ?? COL_PAGE)}{" "}
                        hidden
                      </ColMore>
                    )}
                  </>
                )}
              </ColumnBody>
            </Column>
          );
        })}
      </Scroller>
    </>
  );
};

export default LeadBoard;
export { LeadBoard };
